import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';

import CorrGuardShield from '../shared/CorrGuardShield';
import {
	DEFAULT_SWITCH_COMPANY,
	getActiveCompany,
	setActiveCompany
} from '../../../util/user/companySwitch';
import { fetchCompanyDirectory } from './utils/companyDirectory';

import './SwitchCompanyPage.scss';

/**
 * Lets an internal operator choose which customer company to act as. Once a
 * company is chosen the whole app reloads and every page is scoped to it.
 *
 * Rendered two ways:
 *  - `forced` — a full-screen gate shown right after login until a company is
 *    picked (the operator cannot navigate away until they choose one).
 *  - inline — reached from the sidebar to change the active company later.  A
 *    company is already active in this mode, so the screen offers Cancel:
 *    opening the picker and changing your mind must not force a company change
 *    (nor a guessed navigation) to get back out.
 */
const SwitchCompanyPage = ({ forced = false, history }) => {
	const [companies, setCompanies] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [query, setQuery] = useState('');
	// The company in effect when the screen opened — the one Cancel returns to.
	const [activeCompany] = useState(() => getActiveCompany() || '');
	const [selected, setSelected] = useState(() => getActiveCompany() || '');
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		let cancelled = false;

		// The directory merges the scanline job list with the Postgres customer
		// table and the Cognito `Customer:*` groups, so a company that has only
		// been onboarded for CorrMonitor / XLI / Tracking — or that only has a
		// Cognito group so far — is still switchable.
		const apply = list => {
			if (cancelled) {return;}
			setCompanies(list);
			setError(null);
			setIsLoading(false);
			// Preselect the current company, otherwise the default (Corrpro Canada).
			setSelected(prev => {
				if (prev && list.includes(prev)) {return prev;}
				const preferred = list.find(
					name => name.toLowerCase() === DEFAULT_SWITCH_COMPANY.toLowerCase()
				);
				return preferred || prev || '';
			});
		};

		fetchCompanyDirectory({ onCached: apply })
			.then(list => {
				if (cancelled) {return;}
				if (list.length === 0) {
					setCompanies([]);
					setError('No companies are available for your account.');
					setIsLoading(false);
					return;
				}
				apply(list);
			})
			.catch(() => {
				if (cancelled) {return;}
				setError('Unable to load the list of companies. Please try again.');
				setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) {return companies;}
		return companies.filter(name => name.toLowerCase().includes(q));
	}, [companies, query]);

	const handleConfirm = () => {
		if (!selected || isSaving) {return;}
		setIsSaving(true);
		setActiveCompany(selected);
		// Reload so every page re-reads the new company scope from a clean slate.
		window.location.href = '/all-projects';
	};

	// Cancel is offered only when a company is already active — in the forced gate
	// there is nothing to go back to.  It leaves the stored company exactly as it
	// was (no setActiveCompany call) and returns the user where they came from,
	// falling back to All Projects when this was a direct entry.
	const canCancel = !forced && Boolean(activeCompany);

	const handleCancel = useCallback(() => {
		if (isSaving) {return;}
		if (history && window.history.length > 1) {
			history.goBack();
			return;
		}
		if (history) {
			history.push('/all-projects');
			return;
		}
		window.location.href = '/all-projects';
	}, [history, isSaving]);

	// Escape cancels, matching every other dismissable surface in the app.
	useEffect(() => {
		if (!canCancel) {return undefined;}
		const onKeyDown = event => {
			if (event.key === 'Escape') {handleCancel();}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [canCancel, handleCancel]);

	return (
		<div className={`cg-switch-company${forced ? ' cg-switch-company--forced' : ''}`}>
			<div className="cg-switch-company__card">
				<div className="cg-switch-company__brand">
					<CorrGuardShield size={34} />
					<div className="cg-switch-company__brand-text">
						<span className="cg-switch-company__brand-name">
							<span style={{ color: 'rgba(15,23,42,0.88)' }}>CORR</span>
							<span style={{ color: '#0d9488' }}>GUARD</span>
						</span>
						<span className="cg-switch-company__brand-sub">by Corrpro Canada</span>
					</div>
				</div>

				<h1 className="cg-switch-company__title">
					{forced ? 'Select a company to continue' : 'Switch company'}
				</h1>
				{canCancel && (
					<p className="cg-switch-company__current">
						Currently viewing <strong>{activeCompany}</strong>
					</p>
				)}
				<p className="cg-switch-company__subtitle">
					{forced
						? 'Choose the customer company you want to work in. Everything you see will be scoped to it.'
						: 'Change the customer company you are working in. The app will reload and every page will be scoped to your choice.'}
				</p>

				<input
					type="text"
					className="cg-switch-company__search"
					placeholder="Search companies..."
					value={query}
					onChange={e => setQuery(e.target.value)}
					disabled={isLoading || Boolean(error)}
				/>

				<div className="cg-switch-company__list" role="listbox" aria-label="Companies">
					{isLoading && (
						<div className="cg-switch-company__status">Loading companies...</div>
					)}
					{!isLoading && error && (
						<div className="cg-switch-company__status cg-switch-company__status--error">
							{error}
						</div>
					)}
					{!isLoading && !error && filtered.length === 0 && (
						<div className="cg-switch-company__status">No companies match your search.</div>
					)}
					{!isLoading && !error &&
						filtered.map(name => {
							const isSelected = name === selected;
							return (
								<button
									type="button"
									key={name}
									role="option"
									aria-selected={isSelected}
									className={`cg-switch-company__option${
										isSelected ? ' cg-switch-company__option--selected' : ''
									}`}
									onClick={() => setSelected(name)}
								>
									<span className="cg-switch-company__option-name">{name}</span>
									{isSelected && (
										<span className="cg-switch-company__option-check" aria-hidden="true">
											✓
										</span>
									)}
								</button>
							);
						})}
				</div>

				<div className="cg-switch-company__actions">
					{canCancel && (
						<button
							type="button"
							className="cg-switch-company__cancel"
							onClick={handleCancel}
							disabled={isSaving}
						>
							Cancel
						</button>
					)}
					<button
						type="button"
						className="cg-switch-company__confirm"
						onClick={handleConfirm}
						disabled={
							!selected ||
							isLoading ||
							Boolean(error) ||
							isSaving ||
							// Confirming the company already in effect would reload the whole
							// app to arrive exactly where it started; Cancel is that action.
							selected === activeCompany
						}
						title={
							selected && selected === activeCompany
								? `You are already viewing ${activeCompany}`
								: undefined
						}
					>
						{isSaving ? 'Loading...' : `Continue as ${selected || 'company'}`}
					</button>
				</div>
			</div>
		</div>
	);
};

export default withRouter(SwitchCompanyPage);
